import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { formatDate, formatPercent } from '../../utils/localeFormatters';

/**
 * DashboardCharts - Analytics visualizations for the dashboard
 *
 * Displays:
 * - Weekly activity trend (meetings + lessons)
 * - Payment collection summary
 * - Student/client distribution
 *
 * Props:
 * - meetings: array of meeting objects with date field
 * - lessons: array of lesson objects with scheduledDate field
 * - payments: array of payment objects with paymentDate and amount
 * - students: array of student objects
 * - isTherapy: boolean - therapy vs learning center mode
 */
export default function DashboardCharts({
  meetings = [],
  lessons = [],
  payments = [],
  students = [],
  isTherapy = false,
  attendance = [],
}) {
  const { t, i18n } = useTranslation();
  const hasAttendance = Array.isArray(attendance) && attendance.length > 0 && !isTherapy;
  const [activeChart, setActiveChart] = useState(hasAttendance ? 'attendance' : 'activity');

  // Resolve CSS variable colors for Recharts (which needs raw hex values)
  const getCSSVar = useCallback((varName, fallback) => {
    return getComputedStyle(document.documentElement).getPropertyValue(varName).trim() || fallback;
  }, []);

  const [chartColors, setChartColors] = useState(() => ({
    accent: getCSSVar('--color-chart-accent', '#355760'),
    accentFill: getCSSVar('--color-chart-accent-fill', 'rgba(53, 87, 96, 0.2)'),
    grid: getCSSVar('--color-border', '#e5e7eb'),
    axis: getCSSVar('--color-text-tertiary', '#9ca3af'),
  }));

  // Re-read colors when theme toggles
  useEffect(() => {
    const update = () => setChartColors({
      accent: getCSSVar('--color-chart-accent', '#355760'),
      accentFill: getCSSVar('--color-chart-accent-fill', 'rgba(53, 87, 96, 0.2)'),
      grid: getCSSVar('--color-border', '#e5e7eb'),
      axis: getCSSVar('--color-text-tertiary', '#9ca3af'),
    });
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, [getCSSVar]);

  // Generate activity data for last 7 days
  const getActivityData = () => {
    const days = [];
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const dayLabel = formatDate(date, i18n.language, { weekday: 'short' });

      const dayMeetings = meetings.filter(m => {
        const mDate = typeof m.date === 'string' ? m.date.split('T')[0] : '';
        return mDate === dateStr;
      }).length;

      const dayLessons = lessons.filter(l => {
        const lDate = l.scheduledDate || l.date || '';
        const lDateStr = typeof lDate === 'string' ? lDate.split('T')[0] : '';
        return lDateStr === dateStr;
      }).length;

      days.push({
        name: dayLabel,
        [isTherapy ? t('dashboard.sessions', 'Sessions') : t('sidebar.meetings', 'Meetings')]: dayMeetings,
        ...(isTherapy ? {} : { [t('sidebar.lessons', 'Lessons')]: dayLessons }),
      });
    }
    return days;
  };

  // Generate payment data for last 4 weeks
  const getPaymentData = () => {
    const weeks = [];
    const today = new Date();
    for (let i = 3; i >= 0; i--) {
      const weekStart = new Date(today);
      weekStart.setDate(weekStart.getDate() - (i * 7 + 6));
      const weekEnd = new Date(today);
      weekEnd.setDate(weekEnd.getDate() - (i * 7));

      const weekLabel = `${weekStart.getDate()}/${weekStart.getMonth() + 1}`;

      const weekPayments = payments.filter(p => {
        const pDate = p.paymentDate ? new Date(p.paymentDate?.seconds ? p.paymentDate.seconds * 1000 : p.paymentDate) : null;
        return pDate && pDate >= weekStart && pDate <= weekEnd;
      });

      const totalAmount = weekPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

      weeks.push({
        name: weekLabel,
        [t('jibaya.amount', 'Amount')]: totalAmount,
        count: weekPayments.length,
      });
    }
    return weeks;
  };

  // Generate enrollment/distribution data
  const getDistributionData = () => {
    if (isTherapy) {
      // For therapy: meeting status distribution
      const statusCounts = {};
      meetings.forEach(m => {
        const status = m.status || 'scheduled';
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      });
      return Object.entries(statusCounts).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value,
      }));
    }

    // For learning: student distribution by recent activity
    const months = [];
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const month = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthLabel = formatDate(month, i18n.language, { month: 'short' });
      const monthStudents = students.filter(s => {
        const created = s.createdAt ? new Date(s.createdAt?.seconds ? s.createdAt.seconds * 1000 : s.createdAt) : null;
        return created && created.getMonth() === month.getMonth() && created.getFullYear() === month.getFullYear();
      }).length;
      months.push({
        name: monthLabel,
        [t('dashboard.newStudents', 'New Students')]: monthStudents,
        [t('dashboard.total', 'Total')]: students.filter(s => {
          const created = s.createdAt ? new Date(s.createdAt?.seconds ? s.createdAt.seconds * 1000 : s.createdAt) : null;
          return created && created <= new Date(month.getFullYear(), month.getMonth() + 1, 0);
        }).length,
      });
    }
    return months;
  };

  const activityData = getActivityData();
  const paymentData = getPaymentData();
  const distributionData = getDistributionData();

  const attendanceData = (attendance || []).map((w) => ({
    name: formatDate(w.weekStart, i18n.language, { month: 'short', day: 'numeric' }),
    rate: Math.round((w.rate ?? 0) * 100),
  }));

  const chartTabs = [
    hasAttendance && {
      key: 'attendance',
      label: t('learningDashboard.charts.tab.attendance', 'Attendance'),
    },
    {
      key: 'activity',
      label: isTherapy
        ? t('dashboard.sessionActivity', 'Session Activity')
        : t('learningDashboard.charts.tab.enrollment', 'Weekly Activity'),
    },
    { key: 'payments', label: t('learningDashboard.charts.tab.payments', 'Payments') },
    {
      key: 'distribution',
      label: isTherapy
        ? t('dashboard.sessionStatus', 'Session Status')
        : t('dashboard.enrollment', 'Enrollment'),
    },
  ].filter(Boolean);

  const primaryColor = 'var(--color-primary-dark)';

  return (
    <div style={{
      backgroundColor: 'var(--color-surface)',
      borderRadius: '1rem',
      padding: '1.5rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
    }}>
      {/* Chart Tabs */}
      <div style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        borderBottom: '1px solid var(--color-border)',
        paddingBottom: '0.75rem',
        overflowX: 'auto',
      }}>
        {chartTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveChart(tab.key)}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              background: activeChart === tab.key ? 'var(--color-chart-accent-fill)' : 'transparent',
              color: activeChart === tab.key ? primaryColor : 'var(--color-text-secondary)',
              fontWeight: activeChart === tab.key ? '600' : '400',
              fontSize: '0.875rem',
              borderRadius: '0.375rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s ease',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Charts */}
      <div style={{ width: '100%', height: 280 }}>
        {activeChart === 'attendance' && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={attendanceData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke={chartColors.axis} />
              <YAxis
                tick={{ fontSize: 12 }}
                stroke={chartColors.axis}
                domain={[0, 100]}
                tickFormatter={(v) => formatPercent(v / 100, i18n.language)}
              />
              <Tooltip
                contentStyle={{ borderRadius: '0.5rem', border: `1px solid ${chartColors.grid}`, fontSize: '0.875rem' }}
                formatter={(value) => [formatPercent(value / 100, i18n.language), t('learningDashboard.charts.tab.attendance', 'Attendance')]}
              />
              <Area
                type="monotone"
                dataKey="rate"
                name={t('learningDashboard.charts.tab.attendance', 'Attendance')}
                stroke={chartColors.accent}
                fill={chartColors.accentFill}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}

        {activeChart === 'activity' && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={activityData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke={chartColors.axis} />
              <YAxis tick={{ fontSize: 12 }} stroke={chartColors.axis} allowDecimals={false} />
              <Tooltip
                contentStyle={{ borderRadius: '0.5rem', border: `1px solid ${chartColors.grid}`, fontSize: '0.875rem' }}
              />
              <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
              <Area
                type="monotone"
                dataKey={isTherapy ? t('dashboard.sessions', 'Sessions') : t('sidebar.meetings', 'Meetings')}
                stroke={chartColors.accent}
                fill={chartColors.accentFill}
                strokeWidth={2}
              />
              {!isTherapy && (
                <Area
                  type="monotone"
                  dataKey={t('sidebar.lessons', 'Lessons')}
                  stroke="#3b82f6"
                  fill="rgba(59, 130, 246, 0.15)"
                  strokeWidth={2}
                />
              )}
            </AreaChart>
          </ResponsiveContainer>
        )}

        {activeChart === 'payments' && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={paymentData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke={chartColors.axis} />
              <YAxis tick={{ fontSize: 12 }} stroke={chartColors.axis} />
              <Tooltip
                contentStyle={{ borderRadius: '0.5rem', border: `1px solid ${chartColors.grid}`, fontSize: '0.875rem' }}
                formatter={(value) => [`₪${value}`, t('jibaya.amount', 'Amount')]}
              />
              <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
              <Bar
                dataKey={t('jibaya.amount', 'Amount')}
                fill={chartColors.accent}
                radius={[4, 4, 0, 0]}
                maxBarSize={50}
              />
            </BarChart>
          </ResponsiveContainer>
        )}

        {activeChart === 'distribution' && (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={distributionData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke={chartColors.axis} />
              <YAxis tick={{ fontSize: 12 }} stroke={chartColors.axis} allowDecimals={false} />
              <Tooltip
                contentStyle={{ borderRadius: '0.5rem', border: `1px solid ${chartColors.grid}`, fontSize: '0.875rem' }}
              />
              <Legend wrapperStyle={{ fontSize: '0.8rem' }} />
              {isTherapy ? (
                <Line type="monotone" dataKey="value" stroke={chartColors.accent} strokeWidth={2} dot={{ r: 4 }} />
              ) : (
                <>
                  <Line
                    type="monotone"
                    dataKey={t('dashboard.total', 'Total')}
                    stroke={chartColors.accent}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                  <Line
                    type="monotone"
                    dataKey={t('dashboard.newStudents', 'New Students')}
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                </>
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
